import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import FormError from "../../../../Components/v2/Form/FormError";
import Stats from "../../../../Components/v2/Stats";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import {
  addLocalNotification,
  clearAccountingTransaction,
  clearAcctTransactionError,
  clearDocumentExcelAccountError,
  fetchHasAccounts,
  requestDeleteAcctTransaction,
  requestGetAccountingTransactionStats,
  requestGetParentAccountingTransaction,
  requestListAccountingTransaction,
  requestUpdateAcctTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import PeriodLockBanner from "../../../../Components/v2/Accounting/PeriodLockBanner";
import PeriodLockCell from "../../../../Components/v2/Accounting/PeriodLockCell";
import TableHeader from "../../../../Components/v2/Table/TableHeader";
import { TRANSACTION_TYPES } from "../../../../Constants";
import {
  cancelAPIRequest,
  convertToTitleCase,
  currencyFormat,
  getErrorMessages,
  getHasPaymentsMessage,
  getPaymentCount,
} from "../../../../Utils";
import Table from "../../../../Components/v2/Table";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import TableActions from "../../../../Components/v2/Table/TableActions";
import { isPayrollJournal } from "../../../../Utils/payroll";
import { isDepositTransaction } from "../../../../Utils/DepositUtil";
import { isStockAdjustmentJournal } from "../../../../Utils/AccountingUtil";
import DepositDetailModal from "../../../../Components/v2/Accounting/DepositDetailModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import Modal from "../../../../Components/v2/Modal";
import TransactionModal from "../../../../Components/v2/Accounting/TransactionModal";
import TransactionDetailModal from "../../../../Components/v2/Accounting/TransactionDetailModal";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import { PlusIcon, PencilIcon } from "@heroicons/react/outline";
import InvoiceModal from "../../../../Components/v2/Accounting/InvoiceModal";
import InvoiceDetailModal from "../../../../Components/v2/Accounting/InvoiceDetailModal";
import InvoicePaymentModal from "../../../../Components/v2/Accounting/InvoicePaymentModal";
import FormInput from "../../../../Components/v2/Form/FormInput";
import TransactionUploadModal from "../../../../Components/v2/Accounting/TransactionUploadModal";
import Button from "../../../../Components/v2/Button";
import InitAccountPromptModal from "../../../../Components/v2/Accounting/InitAccountPromptModal";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import { wibStatsMonths } from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const getTransactionTypeInfo = (type) => {
  const matched = TRANSACTION_TYPES.find((item) => item.value === type);
  const fallback = TRANSACTION_TYPES.find((item) => item.value === "OTHER");
  return {
    label: convertToTitleCase(type) || convertToTitleCase(fallback.value),
    ...(matched?.style || fallback.style),
  };
};

const SkeletonStats = () => {
  return (
    <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6 animate-pulse"
        >
          <dt className="text-sm font-medium text-gray-500 truncate">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          </dt>
          <dd className="mt-1 text-3xl font-semibold text-gray-900">
            <div className="h-8 bg-gray-300 rounded w-1/2"></div>
          </dd>
          <dd className="mt-2 flex items-center text-sm text-gray-600">
            <div className="h-4 bg-gray-300 rounded w-1/3"></div>
          </dd>
        </div>
      ))}
    </dl>
  );
};


const AccountingDashboard = () => {
  const { t, i18n } = useTranslation();

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isTransactionDetailModalOpen, setIsTransactionDetailModalOpen] =
    useState(false);
  const [isTransactionUploadModalOpen, setIsTransactionUploadModalOpen] =
    useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isInvoiceDetailModalOpen, setIsInvoiceDetailModalOpen] =
    useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isInvoicePaymentModalOpen, setIsInvoicePaymentModalOpen] =
    useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [transactionType, setTransactionType] = useState(null);
  const [isEditAmountModalOpen, setIsEditAmountModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState(null);

  const accountingDispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();

  const {
    request,
    transactions,
    error,
    loading: accountingLoading,
    accountingStats,
    accountingStatsError,
    accountingStatsLoading,
    deletedID,
    submitError,
    submitLoading,
    transactionID,
    parentTransaction,
    parentTransactionLoading,
  } = useAccountingState();

  const { user } = useAuthState();
  const [depositDetailID, setDepositDetailID] = useState(null);
  const { isDateClosed } = usePeriodLock(user?.mainBusiness?.id);

  // A business with no chart of accounts leaves every transaction form without
  // accounts to pick, so prompt to create one. null = not known yet (or the
  // probe failed — stay silent rather than nag on a network error).
  const [chartEmpty, setChartEmpty] = useState(null);
  const [initSkipped, setInitSkipped] = useState(false);

  useEffect(() => {
    const businessID = user?.mainBusiness?.id;
    setChartEmpty(null);
    setInitSkipped(false);
    if (!businessID) return undefined;
    let cancelled = false;
    fetchHasAccounts(businessID)
      .then((hasAccounts) => {
        if (!cancelled) setChartEmpty(!hasAccounts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.mainBusiness?.id]);

  const showInitPrompt =
    chartEmpty === true &&
    !initSkipped &&
    !isBusinessWriteBlocked(user?.mainBusiness);

  useEffect(() => {
    if (request) {
      cancelAPIRequest(request);
    }
    clearAccountingTransaction(accountingDispatch);
    clearAcctTransactionError(accountingDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoadAccountingStats = useCallback(() => {
    if (user?.mainBusiness?.id) {
      requestGetAccountingTransactionStats(accountingDispatch, {
        businessID: user.mainBusiness.id,
        ...wibStatsMonths(),
      });
    }
  }, [user?.mainBusiness?.id, accountingDispatch]);

  const loadRecentTransactions = () => {
    if (!user?.mainBusiness?.id) return;
    requestListAccountingTransaction(accountingDispatch, {
      businessID: user.mainBusiness.id,
      limit: 5,
    });
  };

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      loadRecentTransactions();
      onLoadAccountingStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingDispatch, user?.mainBusiness?.id, onLoadAccountingStats]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      clearAccountingTransaction(accountingDispatch);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-transaction-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID, accountingDispatch]);

  // Handle inline edit success
  useEffect(() => {
    if (transactionID && isEditAmountModalOpen) {
      addLocalNotification(notificationDispatch, {
        id: `edit-transaction-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.updateSuccess",
        showFull: true,
      });
      setEditingValue("");
      setEditingError(null);
      setIsEditAmountModalOpen(false);
      setSelectedTransaction(null);
      clearAccountingTransaction(accountingDispatch);
    }
  }, [
    transactionID,
    notificationDispatch,
    accountingDispatch,
    isEditAmountModalOpen,
  ]);

  useEffect(() => {
    if (
      submitError &&
      !showConfirmModal &&
      !isEditAmountModalOpen &&
      !isTransactionModalOpen &&
      !isInvoiceModalOpen &&
      !isInvoiceDetailModalOpen &&
      !isInvoicePaymentModalOpen
    ) {
      setShowErrorModal(true);
    }
  }, [
    submitError,
    showConfirmModal,
    isEditAmountModalOpen,
    isTransactionModalOpen,
    isInvoiceModalOpen,
    isInvoiceDetailModalOpen,
    isInvoicePaymentModalOpen,
  ]);

  useEffect(() => {
    if (submitError) {
      setShowConfirmModal(false);
      // A failed amount edit stays open with what was typed and says why, so
      // it can be corrected and sent again.
      if (isEditAmountModalOpen) {
        setEditingError(getErrorMessages(submitError, t).join(" "));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const transformedTransactions = useMemo(
    () =>
      transactions.map((transaction) => {
        const typeInfo = getTransactionTypeInfo(transaction.type);

        return {
          id: transaction.id,
          payrollRunID: transaction.payrollRunID,
          stockAdjustmentID: transaction.stockAdjustmentID,
          depositID: transaction.depositID,
          isDeposit: isDepositTransaction(transaction),
          // Kept for the delete dialog's payment check.
          payments: transaction.payments,
          date: transaction.createdAt,
          description:
            transaction.description ||
            (transaction.partner
              ? `${t("accounting.dashboard.transaction.transactionWith", {
                  partnerName: transaction.partner.name,
                })}`
              : transaction.invoiceNumber
                ? `Invoice #${transaction.invoiceNumber}`
                : null),
          type: {
            label: typeInfo.label,
            colorClass: typeInfo.colorClass,
          },
          amount: {
            total: transaction.total,
            value: currencyFormat(transaction.total),
            colorClass: typeInfo.textColorClass,
            prefix: typeInfo.prefix,
          },
        };
      }),
    [transactions, t],
  );

  const statsData = useMemo(() => {
    const calculatePercentageChange = (current, previous) => {
      const currentValue = typeof current === "number" ? current : 0;
      const previousValue = typeof previous === "number" ? previous : 0;

      // Handle the case where previousValue is 0
      if (previousValue === 0) {
        return {
          change: currentValue,
          direction: currentValue > 0 ? "up" : "no-change",
          color: currentValue > 0 ? "green" : "gray",
        };
      }

      const change = ((currentValue - previousValue) / previousValue) * 100;

      // Check for no change
      if (change === 0) {
        return {
          change: 0,
          direction: "no-change",
          color: "gray",
        };
      }

      return {
        change: change.toFixed(2) + "%",
        direction: change >= 0 ? "up" : "down",
        color: change >= 0 ? "green" : "red",
      };
    };

    const currentIncome = accountingStats?.current?.income || 0;
    const previousIncome = accountingStats?.previous?.income || 0;
    const unpaidInvoice = accountingStats?.current?.unpaidInvoice || 0;
    const pendingInvoice = accountingStats?.current?.pendingInvoice || 0;
    const currentExpense = accountingStats?.current?.expense || 0;
    const previousExpense = accountingStats?.previous?.expense || 0;

    // Calculate percentage changes
    const incomeChange = calculatePercentageChange(
      currentIncome,
      previousIncome,
    );
    const expenseChange = calculatePercentageChange(
      currentExpense,
      previousExpense,
    );

    return [
      {
        title: t("accounting.dashboard.stats.totalRevenue"),
        value: currentIncome,
        footer:
          incomeChange.change === 0
            ? t("accounting.dashboard.stats.noChange")
            : `${
                typeof incomeChange.change === "number"
                  ? new Intl.NumberFormat(i18n.language, {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(incomeChange.change)
                  : incomeChange.change
              } ${
                incomeChange.direction === "up"
                  ? t("accounting.dashboard.stats.increase")
                  : t("accounting.dashboard.stats.decrease")
              }`,
        trend: incomeChange,
      },
      {
        title: t("accounting.dashboard.stats.outstandingInvoices"),
        value: unpaidInvoice,
        footer: t("accounting.dashboard.stats.pendingInvoices", {
          pendingInvoice,
        }),
      },
      {
        title: t("accounting.dashboard.stats.totalExpenses"),
        value: currentExpense,
        footer:
          expenseChange.change === 0
            ? t("accounting.dashboard.stats.noChange")
            : `${
                typeof expenseChange.change === "number"
                  ? new Intl.NumberFormat(i18n.language, {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(expenseChange.change)
                  : expenseChange.change
              } ${
                expenseChange.direction === "up"
                  ? t("accounting.dashboard.stats.increase")
                  : t("accounting.dashboard.stats.decrease")
              }`,
        trend: expenseChange,
        reverseTrendColor: true,
      },
    ];
  }, [accountingStats, t, i18n.language]);

  // Every write entry point re-checks rather than trusting its caller: a row can
  // be locked from another tab between render and click.
  const handleDelete = (transaction) => {
    if (isDateClosed(transaction?.date || transaction?.createdAt)) return;
    if (isPayrollJournal(transaction)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    // Written by /deposit; /transaction refuses to change it.
    if (transaction?.isDeposit || isDepositTransaction(transaction)) return;
    setSelectedTransaction(transaction);
    setShowConfirmModal(true);
  };

  const handleClose = (shouldRefresh) => {
    cancelAPIRequest(request);
    setIsTransactionModalOpen(false);
    setIsTransactionDetailModalOpen(false);
    setSelectedTransaction(null);
    setIsInvoiceModalOpen(false);
    setIsTransactionUploadModalOpen(false);
    clearDocumentExcelAccountError(accountingDispatch);
    if (shouldRefresh && user?.mainBusiness?.id) {
      requestListAccountingTransaction(accountingDispatch, {
        businessID: user.mainBusiness.id,
        limit: 5,
      });
      onLoadAccountingStats();
    }
  };

  const handleEdit = (transaction) => {
    if (isDateClosed(transaction?.createdAt)) return;
    if (isPayrollJournal(transaction)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    // Written by /deposit; /transaction refuses to change it.
    if (transaction?.isDeposit || isDepositTransaction(transaction)) return;
    setSelectedTransaction(transaction);
    if (transaction?.invoiceNumber) {
      setIsInvoiceModalOpen(true);
    } else if (transaction?.parentTransactionID) {
      setSelectedPayment(transaction);
      requestGetParentAccountingTransaction(accountingDispatch, {
        id: transaction.parentTransactionID,
      });
      setIsInvoicePaymentModalOpen(true);
    } else {
      setIsTransactionModalOpen(true);
    }
  };

  const handleStartEdit = (transaction) => {
    if (isDateClosed(transaction?.createdAt)) return;
    if (isPayrollJournal(transaction)) return;
    if (isStockAdjustmentJournal(transaction)) return;
    // Written by /deposit; /transaction refuses to change it.
    if (transaction?.isDeposit || isDepositTransaction(transaction)) return;
    setSelectedTransaction(transaction);
    setEditingValue(transaction.total.toString());
    setEditingError(null);
    setIsEditAmountModalOpen(true);
  };

  const handleCancelEdit = () => {
    cancelAPIRequest(request);
    setSelectedTransaction(null);
    setEditingValue("");
    setEditingError(null);
    setIsEditAmountModalOpen(false);
    // The failure was shown in this modal; don't raise it again on close.
    clearAcctTransactionError(accountingDispatch);
  };

  const handleSaveEdit = () => {
    if (isDateClosed(selectedTransaction?.createdAt)) return;
    if (!editingValue || editingValue.trim() === "") {
      setEditingError("transactions.errors.amountRequired");
      return;
    }

    const newTotal = Number(editingValue);
    if (isNaN(newTotal) || newTotal <= 0) {
      setEditingError("accounting.transactions.modal.error.invalidAmount");
      return;
    }

    setEditingError(null);

    const input = {
      transaction_id: selectedTransaction.id,
      business_id: user.mainBusiness.id,
      accounts:
        selectedTransaction.accounts?.items?.map((account) => ({
          id: account.accountID,
          debit: account.debit > 0 ? newTotal : 0,
          credit: account.credit > 0 ? newTotal : 0,
        })) || [],
      type: selectedTransaction.type,
      paid: selectedTransaction.paid,
      created_at: new Date(selectedTransaction.createdAt),
      description: selectedTransaction.description,
      total: newTotal,
      payment_due_at: new Date(selectedTransaction.paymentDueAt),
      partner_id: selectedTransaction.partnerID || null,
    };

    requestUpdateAcctTransaction(accountingDispatch, { input });
  };

  // The server refuses to delete an invoice or bill that still has payments, so
  // the dialog says so up front and its button stays disabled.
  const selectedPaymentCount = getPaymentCount(selectedTransaction);

  const onDelete = () => {
    if (selectedPaymentCount > 0) return;
    requestDeleteAcctTransaction(accountingDispatch, {
      id: selectedTransaction?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedTransaction) return null;
    const date = dayjs(selectedTransaction.date)
      .locale(i18n.language)
      .format("D MMM YYYY");
    const amount = currencyFormat(selectedTransaction.amount?.total ?? 0);
    const summary = selectedTransaction.description
      ? t("accounting.transactions.modal.description.deleteItem", {
          description: selectedTransaction.description,
          amount,
          date,
        })
      : t("accounting.transactions.modal.description.deleteItemNoDesc", {
          amount,
          date,
        });
    if (selectedPaymentCount === 0) return summary;
    // Spans, not divs: ModalAlert renders this inside a <p>.
    return (
      <>
        {summary}
        <span className="mt-2 block font-medium text-red-600">
          {t("accounting.transactions.modal.description.hasPayments", {
            count: selectedPaymentCount,
          })}
        </span>
      </>
    );
  };

  const dropDownItems = useMemo(
    () => [
      {
        name: t("accounting.dashboard.action.invoice"),
        onClick: () => setIsInvoiceModalOpen(true),
      },
      {
        name: t("accounting.dashboard.action.income"),
        onClick: () => {
          setTransactionType("INCOME");
          setIsTransactionModalOpen(true);
        },
      },
      {
        name: t("accounting.dashboard.action.expense"),
        onClick: () => {
          setTransactionType("EXPENSE");
          setIsTransactionModalOpen(true);
        },
      },
      {
        name: t("transactions.action.upload"),
        onClick: () => {
          setIsTransactionUploadModalOpen(true);
          clearDocumentExcelAccountError(accountingDispatch);
        },
      },
    ],
    [t, accountingDispatch],
  );

  return (
    <div className="space-y-6">
      <AccountingWrapper>
        <main className="lg:col-span-9">
          {/* Page header */}
          <AccountingPageHeader
            title="accounting.dashboard.label"
            dropDownAction={{
              label: t("accounting.dashboard.title.add"),
              onClick: () => {
                setTransactionType(null);
                setIsTransactionModalOpen(true);
              },
              icon: <PlusIcon className="h-5 w-5" />,
              items: dropDownItems,
            }}
          />

          {/* Stats */}
          <div className="mt-6 px-4 sm:px-6 lg:px-8">
            {accountingStatsLoading ? (
              <SkeletonStats />
            ) : accountingStatsError ? (
              <FormError
                error={accountingStatsError}
                title="common.errors.dataTitle"
                onRetry={onLoadAccountingStats}
              />
            ) : (
              <Stats stats={statsData} />
            )}
          </div>

          {/* Recent Transactions */}
          <div className="mt-8 mb-4">
            <div className="px-4 sm:px-6 lg:px-8">
              <TableHeader
                title={t("accounting.dashboard.transaction.header.title")}
                description={t(
                  "accounting.dashboard.transaction.header.description",
                )}
              />
            </div>
            <div className="mt-4 px-4 sm:px-6 lg:px-8">
              <PeriodLockBanner
                businessID={user?.mainBusiness?.id}
                className="mb-4"
              />
              <Table
                isLoading={accountingLoading}
                error={error}
                onRetry={loadRecentTransactions}
                data={transformedTransactions}
                columns={[
                  {
                    header: "accounting.dashboard.transaction.field.date",
                    accessor: (transaction) => (
                      <DateTimeCell value={transaction.date} />
                    ),
                    sortable: true,
                    sortKey: "date",
                  },
                  {
                    header:
                      "accounting.dashboard.transaction.field.description",
                    accessor: (transaction) => {
                      const hasDescription = !!transaction.description;
                      return (
                        <div className="max-w-[200px] lg:max-w-xs whitespace-normal break-words">
                          {hasDescription ? (
                            <ExpandableDescription
                              text={transaction.description}
                            />
                          ) : (
                            <span className="text-gray-400 italic">
                              {t(
                                "accounting.dashboard.transaction.noDescription",
                              )}
                            </span>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    header: "accounting.dashboard.transaction.field.type",
                    accessor: (transaction) => (
                      <span
                        className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${transaction.type.colorClass}`}
                      >
                        {t(transaction.type.label)}
                      </span>
                    ),
                  },
                  {
                    header: "accounting.dashboard.transaction.field.amount",
                    accessor: (transaction, index) => {
                      const originalTransaction = transactions[index];
                      // Clicking the amount opens an edit modal, so a locked row
                      // must lose the click target, hover, cursor, role and
                      // pencil together — the number itself stays readable.
                      const editable =
                        !originalTransaction?.invoiceNumber &&
                        !isPayrollJournal(originalTransaction) &&
                        !isStockAdjustmentJournal(originalTransaction) &&
                        !isDepositTransaction(originalTransaction) &&
                        !isDateClosed(transaction.date);

                      return (
                        <div
                          className={`flex items-center justify-end px-2 py-1 rounded-md transition-colors duration-200 -mr-2 ${
                            editable
                              ? "cursor-pointer group hover:bg-gray-100"
                              : ""
                          }`}
                          onClick={() => {
                            if (editable) {
                              handleStartEdit(originalTransaction);
                            }
                          }}
                          role={editable ? "button" : undefined}
                          title={
                            editable
                              ? t(
                                  "accounting.dashboard.transaction.clickToEdit",
                                )
                              : ""
                          }
                        >
                          <span
                            className={`${transaction.amount.colorClass} font-medium`}
                          >
                            {transaction.amount.prefix}
                            {transaction.amount.value}
                          </span>
                          <div className="w-3.5 h-3.5 ml-2 flex-shrink-0">
                            {editable && (
                              <PencilIcon
                                className="w-full h-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        </div>
                      );
                    },
                    align: "right",
                  },
                  {
                    header: "common.actions.label",
                    accessor: (transaction, index) => {
                      const viewTransaction = () => {
                        if (
                          isDepositTransaction(transactions[index]) &&
                          transactions[index]?.depositID
                        ) {
                          setDepositDetailID(transactions[index].depositID);
                        } else if (transactions[index]?.invoiceNumber) {
                          setSelectedInvoice(transactions[index]);
                          setIsInvoiceDetailModalOpen(true);
                        } else {
                          const tx = transactions[index];
                          setSelectedTransaction(tx);
                          if (tx?.parentTransactionID) {
                            requestGetParentAccountingTransaction(
                              accountingDispatch,
                              { id: tx.parentTransactionID },
                            );
                          }
                          setIsTransactionDetailModalOpen(true);
                        }
                      };

                      // Cell swapped whole: TableActions changes shape between
                      // one action and several, so pruning the list would
                      // rewrite the control instead of just restricting it.
                      if (isDateClosed(transaction.date)) {
                        return (
                          <PeriodLockCell
                            className="justify-end"
                            onView={viewTransaction}
                          />
                        );
                      }

                      if (transaction.isDeposit) {
                        return (
                          <PeriodLockCell
                            className="justify-end"
                            label="deposits.lockedRow"
                            onView={viewTransaction}
                          />
                        );
                      }

                      // A stock-adjustment journal changes only with the stock (Atur Stok
                      // Produk); the server refuses it here.
                      if (isStockAdjustmentJournal(transaction)) {
                        return (
                          <PeriodLockCell
                            className="justify-end"
                            label="accounting.stockAdjustmentJournal.lockedRow"
                            onView={viewTransaction}
                          />
                        );
                      }

                      // A payroll journal changes only on the Payroll page;
                      // the server refuses it here.
                      if (isPayrollJournal(transaction)) {
                        return (
                          <PeriodLockCell
                            className="justify-end"
                            label="payroll.journal.lockedRow"
                            onView={viewTransaction}
                          />
                        );
                      }

                      return (
                        <TableActions
                          position={
                            index === transformedTransactions.length - 1
                              ? "top"
                              : "bottom"
                          }
                          actions={[
                            {
                              label: t("common.view"),
                              onClick: viewTransaction,
                            },
                            {
                              label: t("common.edit"),
                              onClick: () => handleEdit(transactions[index]),
                            },
                            {
                              label: t("common.delete"),
                              onClick: () => handleDelete(transaction),
                              variant: "danger",
                            },
                          ]}
                        />
                      );
                    },
                    align: "right",
                  },
                ]}
              />
            </div>
          </div>

          {/* Delete Modal */}
          <ModalAlert
            open={showConfirmModal}
            setOpen={(open) => {
              setShowConfirmModal(open);
            }}
            title="accounting.transactions.modal.title.delete"
            description="accounting.transactions.modal.description.delete"
            descriptionNode={getDeleteDescription()}
            confirmButtonText="common.delete"
            cancelButtonText="Cancel"
            loadingButton={submitLoading}
            disabledButton={submitLoading || selectedPaymentCount > 0}
            onConfirm={onDelete}
            onCancel={() => {
              cancelAPIRequest(request);
              setShowConfirmModal(false);
              setSelectedTransaction(null);
            }}
          />

          {isTransactionModalOpen && (
            <TransactionModal
              open={isTransactionModalOpen}
              onClose={handleClose}
              transaction={selectedTransaction}
              transactionType={transactionType}
            />
          )}

          {isTransactionDetailModalOpen && (
            <TransactionDetailModal
              open={isTransactionDetailModalOpen}
              onClose={() => {
                setIsTransactionDetailModalOpen(false);
                setSelectedTransaction(null);
                clearAccountingTransaction(accountingDispatch);
              }}
              onEdit={(transaction) => {
                setIsTransactionDetailModalOpen(false);
                handleEdit(transaction);
              }}
              onViewParentInvoice={(parent) => {
                setIsTransactionDetailModalOpen(false);
                setSelectedTransaction(null);
                if (parent?.invoiceNumber) {
                  setSelectedInvoice(parent);
                  setIsInvoiceDetailModalOpen(true);
                } else {
                  setSelectedTransaction(parent);
                  setIsTransactionDetailModalOpen(true);
                }
              }}
              transaction={selectedTransaction}
              parentTransaction={parentTransaction}
              parentTransactionLoading={parentTransactionLoading}
            />
          )}

          {isInvoiceModalOpen && (
            <InvoiceModal
              open={isInvoiceModalOpen}
              onClose={handleClose}
              invoice={selectedTransaction}
            />
          )}

          {isInvoiceDetailModalOpen && (
            <InvoiceDetailModal
              open={isInvoiceDetailModalOpen}
              invoice={selectedInvoice}
              onClose={() => {
                setIsInvoiceDetailModalOpen(false);
                setSelectedInvoice(null);
              }}
              onEdit={(invoice) => {
                setIsInvoiceDetailModalOpen(false);
                setSelectedTransaction(invoice);
                setIsInvoiceModalOpen(true);
              }}
            />
          )}

          {isInvoicePaymentModalOpen && (
            <InvoicePaymentModal
              open={isInvoicePaymentModalOpen}
              payment={selectedPayment}
              parentInvoice={parentTransaction?.id ? parentTransaction : null}
              onClose={() => {
                setIsInvoicePaymentModalOpen(false);
                setSelectedPayment(null);
                clearAccountingTransaction(accountingDispatch);
              }}
            />
          )}

          {isTransactionUploadModalOpen && (
            <TransactionUploadModal
              open={isTransactionUploadModalOpen}
              onClose={handleClose}
            />
          )}

          {showInitPrompt && (
            <InitAccountPromptModal
              open={showInitPrompt}
              businessID={user.mainBusiness.id}
              onSkip={() => setInitSkipped(true)}
              onInitialized={() => setChartEmpty(false)}
            />
          )}

          {depositDetailID && (
            <DepositDetailModal
              open={Boolean(depositDetailID)}
              depositID={depositDetailID}
              businessID={user?.mainBusiness?.id}
              onClose={() => setDepositDetailID(null)}
              // A refund, forfeit, delete or undo changes the recent rows and
              // the month's figures.
              onChanged={() => {
                requestListAccountingTransaction(accountingDispatch, {
                  businessID: user.mainBusiness.id,
                  limit: 5,
                });
                onLoadAccountingStats();
              }}
            />
          )}

          {/* Error Modal */}
          {submitError && (
            <ErrorDialog
              open={showErrorModal}
              message={getHasPaymentsMessage(submitError)}
              error={submitError}
              onClose={() => {
                setShowErrorModal(false);
                clearAcctTransactionError(accountingDispatch);
              }}
            />
          )}

          {/* Edit Amount Modal */}
          <Modal
            open={isEditAmountModalOpen}
            onClose={handleCancelEdit}
            title="accounting.dashboard.transaction.clickToEdit"
            footer={
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={handleCancelEdit}
                  variant="secondary"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={submitLoading}
                  variant="primary"
                >
                  {t("common.save")}
                </Button>
              </div>
            }
          >
            <FormInput
              label={t("transactions.fields.amount")}
              value={editingValue}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !submitLoading) {
                  handleSaveEdit();
                }
              }}
              // The plain number is kept, as in TransactionModal: fed back as
              // "3.000", the formatted text had its "." read as a decimal point.
              numberFormat={{
                thousandSeparator: ".",
                decimalSeparator: ",",
                allowedDecimalSeparators: [","],
                valueIsNumericString: true,
                onValueChange: ({ value }) => {
                  setEditingValue(value);
                  if (editingError) setEditingError(null);
                },
              }}
              prefix="Rp"
              error={editingError}
              disabled={submitLoading}
              onClear={() => setEditingValue("")}
              autoFocus
            />
          </Modal>
        </main>
      </AccountingWrapper>
    </div>
  );
};

export default AccountingDashboard;
